import { LoadingState } from "@repo/app-types/client/client";
import type { ApiKeyWithFullKey } from "@repo/app-types/entities/apiKey";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { PH_NO_CAPTURE_CLASS } from "../../analytics/sessionRecordingMasking.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateApiKeyModal: FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [apiKeyName, setApiKeyName] = useState<string>("");
  const [createApiKeyLoadingState, setCreateApiKeyLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);
  const [newApiKey, setNewApiKey] = useState<ApiKeyWithFullKey | undefined>(
    undefined,
  );

  const onClickCreateApiKey = async () => {
    setCreateApiKeyLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.post(`/api-keys`, {
        name: apiKeyName,
      });

      setNewApiKey(data);
      setCreateApiKeyLoadingState(LoadingState.LOADED);
    } catch (err) {
      setCreateApiKeyLoadingState(LoadingState.ERROR);
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>
        {newApiKey ? "API key created" : "Create an API key"}
      </ModalHeader>

      {newApiKey ? (
        <>
          <div className="mt-6 mb-2">
            <p className="text-sm text-foreground">
              Copy your API key and store it in a safe place. You won't be able
              to see it again.
            </p>
            <div
              className={twMerge(
                "mt-4 mb-4 rounded-lg bg-gray-100 p-4 text-sm break-words",
                PH_NO_CAPTURE_CLASS,
              )}
            >
              {newApiKey.key}
            </div>
            <Button
              variant={ButtonVariant.Primary}
              onClick={() => {
                navigator.clipboard.writeText(newApiKey.key);
                showNotification(dispatch, {
                  id: `api-key-copied-${new Date().getTime()}`,
                  primaryMessage: `API key copied to clipboard`,
                  type: NotificationType.SUCCESS,
                });
              }}
            >
              Copy
            </Button>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <Button variant={ButtonVariant.Secondary} onClick={() => onClose()}>
              Done
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-6 mb-2">
            <Input
              label="API key name"
              autoFocus={true}
              onChange={(evt) => {
                setApiKeyName(evt.target.value);
              }}
              value={apiKeyName}
              description="Provide a name that describes how this API key will be used."
              maxLength={100}
            />
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <div className="ml-3">
              <Button
                variant={ButtonVariant.Primary}
                isDisabled={!apiKeyName}
                onClick={() => onClickCreateApiKey()}
                isLoading={createApiKeyLoadingState === LoadingState.LOADING}
              >
                Create API Key
              </Button>
            </div>
            <Button variant={ButtonVariant.Secondary} onClick={() => onClose()}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
